type WaitForAllSettledReason = "complete" | "empty" | "timeout" | "reset";

/**
 * A promise orchestrator that currently primarily exists to allow you to wait
 * for an arbitrary list of promises to all settle before proceeding with
 * follow-up logic. The main difference between this and something like `Promise.allSettled`
 * is that these promises can be created from anywhere and don't need to all be in
 * one place - they just need to be added to the orchestrator's queue via `add()`.
 */
export class PromiseOrchestrator {
  private queue: Promise<unknown>[];

  /**
   * An array that contains all the `resolve` functions from all `waitForAllSettled`
   * calls that are still waiting.
   */
  private waitForAllSettledResolveFns: ((
    value: WaitForAllSettledReason | PromiseLike<WaitForAllSettledReason>,
  ) => void)[];

  constructor() {
    this.queue = [];
    this.waitForAllSettledResolveFns = [];
  }

  /**
   * Add a `Promise` to this orchestrator's queue. This primarily means that
   * calling the orchestrator's `waitForAllSettled` method will wait until this
   * promise (and all other promises in the orchestrator's queue) is settled.
   */
  add(promise: Promise<unknown>): void {
    this.queue.push(promise);

    promise.finally(() => {
      // If the promise has since been removed from the queue, do not process it.
      // This could happen if, e.g., `reset()` was called.
      if (!this.queue.includes(promise)) return;

      this.queue = this.queue.filter((item) => item !== promise);
      if (this.queue.length === 0) {
        this.waitForAllSettledResolveFns.forEach((resolveFn) => {
          resolveFn("complete");
        });
        this.waitForAllSettledResolveFns = [];
      }
    });
  }

  /**
   * Resets this orchestrator by removing all of its promises. Any pending
   * `waitForAllSettled` calls will be resolved with the reason being `'reset'`.
   */
  reset(): void {
    this.queue = [];
    this.waitForAllSettledResolveFns.forEach((resolveFn) => {
      resolveFn("reset");
    });
    this.waitForAllSettledResolveFns = [];
  }

  /**
   * Wait for all promises in this orchestrator to settle. If a timeout is specified
   * and the timeout duration elapses before all promises have settled, this function
   * will resolve with the reason being `'timeout'`.
   */
  async waitForAllSettled(timeout?: number): Promise<WaitForAllSettledReason> {
    if (this.queue.length === 0)
      return Promise.resolve<WaitForAllSettledReason>("empty");

    return new Promise((resolve) => {
      if (timeout != null) {
        setTimeout(() => {
          resolve("timeout");
        }, timeout);
      }

      this.waitForAllSettledResolveFns.push(resolve);
    });
  }
}
